import React, { FC, useCallback, useEffect, useState } from 'react';
import apiUrl from '../../assets/apiUrl';
import { decode } from 'jsonwebtoken';

interface SignupOptions {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface IAuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (login: string, password: string) => void;
  logout: () => void;
  signup: (options: SignupOptions) => Promise<Response>;
  fetchWrapper: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  _login: (token: string) => void;
}

const defaultState = {
  isAuthenticated: false,
  isLoading: true,
  login: (login: string, password: string) => {},
  getAccessTokenSilently: (): Promise<string> => {
    return new Promise((resolve, _reject) => {
      resolve('');
    });
  },
  logout: () => {},
  signup: (options: SignupOptions) => {
    return fetch(`${apiUrl}/auth/register`);
  },
  fetchWrapper: (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    return new Promise(async (resolve, reject) => {
      return await fetch(input, init);
    });
  },
  _login: (token: string) => {},
};

const AuthContext = React.createContext<IAuthContext>(defaultState);

export const AuthProvider: FC = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const login = (login: string, password: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${apiUrl}/auth/login`, {
          method: 'POST',
          credentials: 'include',
          body: new URLSearchParams({
            email: login,
            password,
          }),
        });
        if (!response.ok) {
          throw response.statusText;
        }
        const data = await response.json();
        sessionStorage.setItem('access-token', data.token);
        setIsAuthenticated(true);
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  };
  useEffect(() => {
    (async () => {
      try {
        await getAccessToken();
        setIsAuthenticated(true);
      } catch (err) {
        logout();
      }
      setIsLoading(false);
    })();
  }, []);
  const logout = async () => {
    setIsLoading(true);
    await fetch(`${apiUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    sessionStorage.removeItem('access-token');
    setIsAuthenticated(false);
    setIsLoading(false);
  };
  const fetchWrapper = useCallback(
    (input: RequestInfo, init?: RequestInit | undefined): Promise<Response> => {
      return new Promise(async (resolve, reject) => {
        let res: Response;
        try {
          res = await fetch(input, {
            headers: {
              'x-access-token': await getAccessToken(),
            },
            ...init,
          });
          if (!res.ok) {
            throw res.statusText;
          }
        } catch (err) {
          let newToken = await fetch(`${apiUrl}/auth/refresh_token`, {
            method: 'POST',
            credentials: 'include',
          });
          if (!newToken.ok) {
            return logout();
          }
          sessionStorage.setItem(
            'access-token',
            (await newToken.json()).accessToken
          );
          res = await fetch(input, {
            ...init,
            headers: {
              'x-access-token': sessionStorage.getItem('access-token') || '',
            },
          });
        }
        resolve(res);
      });
    },
    []
  );
  const signup = (options: SignupOptions): Promise<Response> => {
    return new Promise(async (resolve, reject) => {
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        body: new URLSearchParams({
          first_name: options.firstName,
          last_name: options.lastName,
          email: options.email,
          password: options.password,
        }),
      });

      if (!response.ok) {
        return reject(response.statusText);
      }
      resolve(response);
    });
  };
  const _login = (token: string) => {
    sessionStorage.setItem('access-token', token);
    setIsAuthenticated(true);
  };
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        fetchWrapper,
        signup,
        _login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

let accessToken = ''
const getAccessToken = async () => {
  // If key exists we check the expiration date and if it's valid
  // we return it, otherwise we generate a new one
  if (accessToken) {
    const decoded = decode(accessToken, { json: true });
    if (decoded && decoded.exp && decoded.exp >= (Date.now() / 1000)) return accessToken;
  }
  const response = await fetch(`${apiUrl}/auth/refresh_token`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw 'Failed';
  accessToken =  (await response.json()).accessToken;
  return accessToken;
};

export { getAccessToken };
